import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { 
  Compass, 
  Search, 
  BookOpen, 
  Users, 
  User,
  ChevronRight,
  X,
  Sparkles,
  Home,
  CheckCircle
} from "lucide-react";

const WALKTHROUGH_STEPS = [
  {
    id: "welcome",
    type: "modal",
    title: "Welcome to Compass",
    description: "Your personal transformation starts here.",
    icon: Compass,
    buttonText: "Show me around",
    skipText: "Skip"
  },
  {
    id: "home",
    type: "highlight",
    targetPath: "/compass",
    navItem: "Home",
    title: "Your Daily Base",
    description: "This is your daily base. Check in, see your signal, and track your progress every day.",
    icon: Home,
    position: "center"
  },
  {
    id: "discover",
    type: "highlight",
    targetPath: "/discover",
    navItem: "Discover",
    title: "Explore & Discover",
    description: "Explore tools, meditations, soundscapes, and your transformation quests here.",
    icon: Search,
    position: "center"
  },
  {
    id: "reflections",
    type: "highlight",
    targetPath: "/reflections",
    navItem: "Reflections",
    title: "Your Journal",
    description: "Your journal lives here. Everything you log is saved and private.",
    icon: BookOpen,
    position: "center"
  },
  {
    id: "community",
    type: "highlight",
    targetPath: "/community",
    navItem: "Community",
    title: "Connect & Share",
    description: "Connect with others on the same journey.",
    icon: Users,
    position: "center"
  },
  {
    id: "profile",
    type: "highlight",
    targetPath: "/profile",
    navItem: "Profile",
    title: "Your Progress",
    description: "Track your badges, streaks, and manage your subscription here.",
    icon: User,
    position: "center"
  },
  {
    id: "complete",
    type: "modal",
    title: "You're Ready!",
    description: "Start with today's check-in.",
    icon: CheckCircle,
    buttonText: "Let's Begin",
    finalStep: true
  }
];

export function OnboardingWalkthrough({ onComplete }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, updateUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const step = WALKTHROUGH_STEPS[currentStep];

  // Navigate to the target path when step changes
  useEffect(() => {
    if (step?.type === "highlight" && step.targetPath && location.pathname !== step.targetPath) {
      navigate(step.targetPath);
    }
  }, [currentStep, step, navigate, location.pathname]);

  const handleNext = () => {
    if (currentStep < WALKTHROUGH_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeWalkthrough();
    }
  };

  const handleSkip = () => {
    completeWalkthrough();
  };

  const completeWalkthrough = async () => {
    try {
      await axios.post(`${API}/user/walkthrough/complete`, {}, { withCredentials: true });
      // Update local user state
      if (user) {
        updateUser({ ...user, walkthrough_completed: true });
      }
    } catch (error) {
      console.error("Failed to mark walkthrough complete:", error);
    }
    
    setIsVisible(false);
    navigate("/compass");
    onComplete?.();
  };

  if (!isVisible) return null;

  // Welcome/Complete Modal
  if (step.type === "modal") {
    const Icon = step.icon;
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-[#0B2821]/95 backdrop-blur-sm" />
        
        {/* Modal */}
        <div 
          className="relative z-10 mx-4 max-w-md w-full animate-fade-in"
          style={{ animationDuration: "0.3s" }}
        >
          <div className="bg-gradient-to-br from-[#2d4a44] to-[#143d33] rounded-2xl border border-[#a69373]/30 shadow-2xl overflow-hidden">
            {/* Decorative header */}
            <div className="h-2 bg-gradient-to-r from-[#a69373] via-[#d4b87a] to-[#a69373]" />
            
            <div className="p-8 text-center">
              {/* Icon */}
              <div className="w-20 h-20 rounded-full bg-[#a69373]/20 flex items-center justify-center mx-auto mb-6">
                <Icon className="w-10 h-10 text-[#a69373]" />
              </div>
              
              {/* Content */}
              <h2 className="text-2xl font-heading text-[#a69373] mb-3">{step.title}</h2>
              <p className="text-white/70 text-lg mb-8">{step.description}</p>
              
              {/* Actions */}
              <div className="space-y-3">
                <Button
                  onClick={handleNext}
                  className="w-full py-6 text-lg font-semibold bg-[#a69373] hover:bg-[#b89a5e] text-[#0B2821] rounded-xl"
                  data-testid="walkthrough-next-btn"
                >
                  {step.buttonText}
                  {!step.finalStep && <ChevronRight className="w-5 h-5 ml-2" />}
                </Button>
                
                {step.skipText && (
                  <button
                    onClick={handleSkip}
                    className="text-white/50 hover:text-white/70 text-sm transition-colors"
                    data-testid="walkthrough-skip-btn"
                  >
                    {step.skipText}
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Step indicator for welcome */}
          {!step.finalStep && (
            <div className="flex justify-center gap-2 mt-6">
              {WALKTHROUGH_STEPS.map((s, idx) => (
                <div
                  key={s.id}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentStep 
                      ? "bg-[#a69373] w-6" 
                      : idx < currentStep 
                        ? "bg-[#a69373]/60" 
                        : "bg-white/20"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Highlight/Tooltip overlay
  if (step.type === "highlight") {
    const Icon = step.icon;
    return (
      <div className="fixed inset-0 z-[100] pointer-events-none">
        {/* Semi-transparent overlay */}
        <div className="absolute inset-0 bg-[#0B2821]/80" />
        
        {/* Content card - centered */}
        <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-auto">
          <div 
            className="max-w-sm w-full animate-fade-in"
            style={{ animationDuration: "0.3s" }}
          >
            <div className="bg-gradient-to-br from-[#2d4a44] to-[#143d33] rounded-2xl border border-[#a69373]/30 shadow-2xl overflow-hidden">
              {/* Progress bar */}
              <div className="h-1 bg-[#a69373]/10">
                <div 
                  className="h-full bg-[#a69373] transition-all duration-300"
                  style={{ width: `${((currentStep + 1) / WALKTHROUGH_STEPS.length) * 100}%` }}
                />
              </div>
              
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#a69373]/20 flex items-center justify-center shrink-0">
                    <Icon className="w-6 h-6 text-[#a69373]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[#a69373]/60 text-xs font-medium uppercase tracking-wider">
                        {step.navItem}
                      </span>
                      <button
                        onClick={handleSkip}
                        className="p-1 rounded-lg hover:bg-white/10 transition-colors"
                        data-testid="walkthrough-close-btn"
                      >
                        <X className="w-4 h-4 text-white/40" />
                      </button>
                    </div>
                    <h3 className="text-xl font-heading text-white mt-1">{step.title}</h3>
                  </div>
                </div>
                
                {/* Description */}
                <p className="text-white/70 mb-6 leading-relaxed">{step.description}</p>
                
                {/* Actions */}
                <div className="flex items-center justify-between">
                  <span className="text-white/40 text-sm">
                    {currentStep + 1} of {WALKTHROUGH_STEPS.length}
                  </span>
                  <Button
                    onClick={handleNext}
                    className="px-6 py-2 bg-[#a69373] hover:bg-[#b89a5e] text-[#0B2821] font-semibold rounded-xl"
                    data-testid="walkthrough-continue-btn"
                  >
                    Continue
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Pointer arrow pointing down to navigation */}
            <div className="flex justify-center mt-4">
              <div className="w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-t-[12px] border-t-[#a69373]/30" />
            </div>
            
            {/* Hint text */}
            <p className="text-center text-white/40 text-sm mt-2">
              Look at <span className="text-[#a69373]">{step.navItem}</span> in the navigation below
            </p>
          </div>
        </div>
        
        {/* Bottom navigation highlight area - allow clicks through */}
        <div className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-t from-transparent to-[#2d4a44]/80" />
        </div>
      </div>
    );
  }

  return null;
}

export default OnboardingWalkthrough;
